import { events } from 'fetch-event-stream';
import { toError } from '@astlai/common/util/error-handling';
import type {
	ChatMessage,
	ClassificationInfo,
	RetrievalContext,
	WebSourceDocument,
	SourceDocument,
	ToolCall,
	TopicInfo
} from 'src/types/llm';

export interface StreamEvent {
	message: ChatMessage;
	eventData: Record<string, unknown>;
	eventType?: string;
	sources?: SourceDocument[];
	webSources?: WebSourceDocument[];
	retrievalContext?: RetrievalContext;
}

export async function* processStreamedResponse(
	response: Response,
	tempMessage: ChatMessage,
	abortController?: AbortController
): AsyncGenerator<StreamEvent> {
	const stream = events(response, abortController?.signal);
	let message = { ...tempMessage };

	for await (const event of stream) {
		const eventType = event.event;
		const eventData = JSON.parse(event.data);

		switch (eventType) {
			case 'conversation.created':
				break;
			case 'rag.started':
				message.status = eventType;
				message.id = (eventData.message_id as string) || message.id;
				break;
			case 'rag.reasoning':
				message.status = 'reasoning';
				message.reasoning_content = (message.reasoning_content || '').concat(
					eventData.reasoning_content || ''
				);
				break;
			case 'rag.content':
				message.status = 'in_progress';
				message.content = message.content.concat((eventData.content as string) || '');
				message.currentToolCallIndex = undefined;
				break;
			case 'rag.done':
				message.status = 'completed';
				break;
			case 'rag.retrieval_context':
				// Emit retrieval context for client-side storage (not persisted)
				yield { message, eventData, eventType, retrievalContext: eventData as RetrievalContext };
				continue;
			case 'rag.sources':
				message.status = eventType;
				// Emit sources for client-side storage (persisted)
				yield { message, eventData, eventType, sources: eventData.sources as SourceDocument[] };
				continue;
			case 'rag.web_sources':
				message.status = eventType;
				yield {
					message,
					eventData,
					eventType,
					webSources: eventData.web_sources as WebSourceDocument[]
				};
				continue;
			case 'rag.tool_call': {
				message.status = eventType;
				const toolCall = eventData as ToolCall;
				message.toolCalls = [...(message.toolCalls || []), toolCall];
				message.currentToolCallIndex = message.toolCalls.length - 1;
				break;
			}
			case 'rag.tool_result': {
				message.status = eventType;
				const { call_id, result_count } = eventData as { call_id: string; result_count: number };
				const toolCall = message.toolCalls?.find((tc) => tc.call_id === call_id);
				if (toolCall) {
					toolCall.result_count = result_count;
				}
				break;
			}
			case 'rag.available_tools':
				message.available_tools = eventData.tools as string[];
				break;
			case 'rag.classification':
				message.classification = eventData as ClassificationInfo;
				break;
			case 'rag.topic':
				message.topic = eventData as unknown as TopicInfo;
				break;
			case 'rag.timing':
				message.timings = eventData as { total_seconds: number; steps?: Record<string, number> };
				break;
			case 'rag.error':
			case 'error':
				throw toError(eventData);
		}

		yield { message, eventData, eventType };
	}

	return message;
}
